import React, { useEffect, useMemo, useCallback } from 'react';
import {
  Outlet,
  RouteObject,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Button, Layout, Loading } from 'tdesign-react';
import { useGlobalStore } from '@/stores/context';
import { PageStatus } from '@/stores/provider';
import { ErrorBoundary } from 'react-error-boundary';
// import errorSrc from '@/assets/imgs/2.png';
import MyHeader from '@/components/Layouts/header';
import Menus from '@/components/Menus';
import { AppIcon, AssignmentIcon, DataBaseIcon, InstitutionCheckedIcon, LaptopIcon, NotificationIcon, PersonalInformationIcon, Setting1Icon, UserIcon, UserUnknownIcon, VideoIcon, TableIcon, } from 'tdesign-icons-react';
import classNames from 'classnames';
import { temporaryLogin } from './utils';
import { authUserInfo } from './api';

const isTemporaryLogin = temporaryLogin();

const { Content, Header, Footer } = Layout;


export interface RouteItemCommonProps {
  isInMenu?: boolean; // true: 显示菜单，菜单有选中状态; false: 不显示菜单; undefined: 显示菜单，菜单无选中态或选中父菜单;
  title: string;
  path: string;
  code?: string;
  icon?: React.ReactElement<unknown, string | React.JSXElementConstructor<any>>;
  lazy?: () => Promise<{ default: () => React.ReactElement }>;
  permission?: boolean;
  element?: React.ReactElement;
  children?: RouteItemCommonProps[];
}

// 带菜单的路由
const menuRoutes: Array<RouteItemCommonProps> = [
  {
    path: '/home',
    title: '首页',
    icon: <AppIcon />,
    permission: true,
    code: 'R1164001',
    lazy: () => import('@/pages/Home'),
  },
  // {
  //   path: '/user',
  //   title: '用户管理',
  //   icon: <LaptopIcon />,
  //   code: '1',
  //   lazy: () => import('@/pages/User'),
  // },
  // {
  //   path: '/permission',
  //   title: '权限管理',
  //   icon: <PersonalInformationIcon />,
  //   lazy: () => import('@/pages/User'),
  // },
  {
    path: '/examine',
    title: '审核管理',
    code: 'R1164002',
    icon: <InstitutionCheckedIcon />,
    lazy: () => import('@/pages/Home'),
  },
  {
    path: '/video',
    title: '视频管理',
    code: 'R1164003',
    icon: <VideoIcon />,
    lazy: () => import('@/pages/Video'),
  },
  // {
  //   path: '/notice',
  //   title: '通知管理',
  //   icon: <NotificationIcon />,
  //   lazy: () => import('@/pages/Notice'),
  // },
  {
    path: '/guidingPeople',
    title: '数字人管理',
    code: 'R1164004',
    icon: <UserUnknownIcon />,
    lazy: () => import('@/pages/GuidingPeople'),
  },
  {
    path: '/invite',
    title: '邀请管理',
    icon: <UserIcon />,
    code: 'R1164005',
    lazy: () => import('@/pages/Invite'),
  },
  {
    path: '/process',
    title: '流程管理',
    icon: <AssignmentIcon />,
    code: 'R1164006',
    lazy: () => import('@/pages/Process'),
  },
  {
    path: '/dictionary',
    title: '字典数据',
    icon: <DataBaseIcon />,
    code: 'R1164007',
    lazy: () => import('@/pages/Dictionary'),
  },
  {
    path: '/questions',
    title: '问题库',
    icon: <TableIcon />,
    code: 'R1164008',
    lazy: () => import('@/pages/Questions'),
  },
  // {
  //   path: '/user11',
  //   title: '系统设置管理',
  //   icon: <Setting1Icon />,
  //   lazy: () => import('@/pages/User'),
  // },

  {
    isInMenu: false,
    path: '/error',
    title: '无权限',
    lazy: () => import('@/pages/Error'),
  },
  {
    isInMenu: false,
    path: '/login',
    title: '未登录',
    lazy: () => import('@/pages/Login'),
  },
  {
    path: '*',
    title: '404',
    isInMenu: false,
    element: <div style={{ padding: 32, textAlign: 'center' }}>找不到页面</div>,
  },
]

function Container(): React.ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    state: {
      pageStatus,
      userInfo,
    },
    dispatch,
  } = useGlobalStore();

  const resources = useMemo(() => {
    if (isTemporaryLogin || !Object.keys(userInfo || {}).length) {
      return [];
    }
    const roleAuthorities = userInfo.userAuthor?.appAuthorities.find(item => item.appId === process.env.APPID)?.roleAuthorities || [];;
    const v = roleAuthorities.find(item => item.resources?.[0]?.appId === process.env.APPID && menuRoutes.some(ele => ele.code === item.resources?.[0]?.code));
    if (v) {
      return v.resources;
    }
    return [];
  }, [userInfo]);

  useEffect(() => {
    if (isTemporaryLogin || pageStatus === PageStatus.NOLOGIN) return;
    const fetchUserInfo = async () => {
      try {
        const res = await authUserInfo();
        if (res.code === 0) {
          sessionStorage.setItem('videoAudit-b-userInfo', JSON.stringify(res.data));
          dispatch({
            type: 'setData',
            data: {
              userInfo: res.data,
            }
          });
        }
      } catch (error) {
      }
    };
    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (pathname === '/') {
      navigate('/home');
      return;
    }
    // 已登录状态就不允许跳转登录页面
    if ((pageStatus === PageStatus.NORMAL) && pathname === '/login') {
      // 跳转到权限的第一个页面
      const page = menuRoutes.find(item => item.code && item.code === resources[0]?.code);
      if (page) {
        navigate(page.path);
      } else {
        navigate('/home');
      }
      return;
    }
    if (pageStatus === PageStatus.NOLOGIN && pathname !== '/login') {
      navigate(`/login?redirect=${location.pathname}${location.search}`);
      return;
    }
    // 无权限
    const flag = resources.some((item: any) => item.code === menuRoutes.find(item => item.path === pathname)?.code);
    const flag2 = menuRoutes.some(item => item.path === pathname);
    if (!flag && flag2 && pathname !== '/login' && !isTemporaryLogin) {
      navigate('/error');
      return;
    }
    if ((pageStatus === PageStatus.ERROR) && pathname !== '/error') {
      navigate('/error');
    }
  }, [pageStatus, pathname, navigate]);


  const noShowHeader = useMemo(() => {
    return pathname === '/login';
  }, [pathname]);

  const pageTitle = useMemo(() => {
    return menuRoutes.find(item => item.path === pathname)?.title || '';
  }, [pathname]);

  const fallback = useMemo(
    () => (
      <div className='error-page-ewrapper'>
        {/* <img src={errorSrc} width={60} height={60} /> */}
        <div className='error-page-tip'>
          <div>页面异常,请稍后重试.</div>
          <div className='tip1'>
            如果问题持续存在，请 <span className='tip2' >反馈</span> 给我们，我们会尽快帮您解决。
          </div>
        </div>
        <Button className='goto-main' onClick={() => location.replace(`/`)} >首页</Button>
      </div>
    ),
    [],
  );
  const onError = useCallback((error: Error) => {
    console.log('error', error);
  }, []);
  // if (pageStatus === PageStatus.NOLOGIN && pathname !== '/login') return <Loading loading={true} />;

  return (
    <Layout
      style={{ height: '100%' }}
    >
      {
        !noShowHeader && (
          <Header className={`v-header}`} >
            <MyHeader pageTitle={pageTitle} />
          </Header>)
      }
      <Content
        className='v-content'
      >
        <div className='v-content-main'>
          {!noShowHeader && <Menus menuList={menuRoutes} />}
          <ErrorBoundary fallback={fallback} onError={onError}>
            <div className={classNames("v-body", { "v-body-spec": !noShowHeader })}>
              <Outlet></Outlet>
            </div>
          </ErrorBoundary>
        </div>
      </Content>
    </Layout>
  );
}

const Routes: Array<RouteObject> = [
  // 如果有独立页面路由可以加在这里
  {
    path: '/',
    element: <Container />,
    children: menuRoutes as unknown as RouteObject[],
  },
];
// const Routes: Array<RouteObject> = menuRoutes as unknown as RouteObject[];

export default Routes;
